/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Task, TaskRecurrence } from '../types';

/**
 * Format a human-readable German label for a task recurrence config.
 */
export function formatRecurrenceLabel(recurrence?: TaskRecurrence): string {
  if (!recurrence || recurrence.frequency === 'none') return '';
  const interval = recurrence.interval || 1;
  const weekdayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

  let rhythm = '';
  switch (recurrence.frequency) {
    case 'daily':
      rhythm = interval === 1 ? 'Täglich' : `Alle ${interval} Tage`;
      break;
    case 'weekly': {
      const days = recurrence.weekdays && recurrence.weekdays.length > 0
        ? ` (${recurrence.weekdays.map(d => weekdayNames[d]).join(', ')})`
        : '';
      rhythm = interval === 1 ? `Wöchentlich${days}` : `Alle ${interval} Wochen${days}`;
      break;
    }
    case 'monthly':
      rhythm = interval === 1 
        ? (recurrence.monthDay ? `Monatlich (am ${recurrence.monthDay}.)` : 'Monatlich')
        : `Alle ${interval} Monate`;
      break;
    case 'yearly':
      rhythm = interval === 1 ? 'Jährlich' : `Alle ${interval} Jahre`;
      break;
    default:
      return '';
  }

  if (recurrence.endType === 'after_occurrences' && recurrence.endOccurrences) {
    return `${rhythm} (${recurrence.count || 1}/${recurrence.endOccurrences}x)`;
  }
  if (recurrence.endType === 'on_date' && recurrence.endDate) {
    const parts = recurrence.endDate.split('-');
    const formattedDate = parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : recurrence.endDate;
    return `${rhythm} (bis ${formattedDate})`;
  }

  return rhythm;
}

/**
 * Helper to format a Date object as YYYY-MM-DD in local time
 */
function toDateStr(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Helper to parse YYYY-MM-DD string into a local Date at 00:00:00
 */
function parseDateStr(str: string): Date {
  const [y, m, d] = str.split('-').map(num => parseInt(num, 10));
  return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
}

/**
 * Computes the next occurrence start/end dates for a given task.
 * Returns null if the recurrence is inactive or has reached its end condition.
 */
export function computeNextOccurrenceDates(task: Partial<Task>): { nextStart: string; nextEnd: string; nextCount: number } | null {
  const recurrence = task.recurrence;
  if (!recurrence || recurrence.frequency === 'none') {
    return null;
  }

  const currentCount = recurrence.count || 1;
  if (recurrence.endType === 'after_occurrences' && recurrence.endOccurrences) {
    if (currentCount >= recurrence.endOccurrences) {
      return null;
    }
  }

  const baseStartStr = task.start || toDateStr(new Date());
  const baseStartDate = parseDateStr(baseStartStr);

  let durationDays = 0;
  if (task.start && task.end) {
    const baseEndDate = parseDateStr(task.end);
    const diffMs = baseEndDate.getTime() - baseStartDate.getTime();
    durationDays = Math.max(0, Math.round(diffMs / (1000 * 60 * 60 * 24)));
  }

  const interval = Math.max(1, recurrence.interval || 1);
  let nextStartDate = new Date(baseStartDate);

  switch (recurrence.frequency) {
    case 'daily': {
      nextStartDate.setDate(nextStartDate.getDate() + interval);
      break;
    }
    case 'weekly': {
      const weekdays = recurrence.weekdays && recurrence.weekdays.length > 0 
        ? [...recurrence.weekdays].sort((a, b) => a - b)
        : null;

      if (weekdays && weekdays.length > 0) {
        const curDay = nextStartDate.getDay();
        // Look for next weekday in current week
        const nextDayInWeek = weekdays.find(d => d > curDay);

        if (nextDayInWeek !== undefined) {
          nextStartDate.setDate(nextStartDate.getDate() + (nextDayInWeek - curDay));
        } else {
          // Jump to first weekday in the next interval week
          const daysToWeekendEnd = 7 - curDay;
          const jumpWeeks = (interval - 1) * 7;
          const daysToFirstDay = weekdays[0];
          nextStartDate.setDate(nextStartDate.getDate() + daysToWeekendEnd + jumpWeeks + daysToFirstDay);
        }
      } else {
        nextStartDate.setDate(nextStartDate.getDate() + (interval * 7));
      }
      break;
    }
    case 'monthly': {
      const targetMonth = nextStartDate.getMonth() + interval;
      const targetYear = nextStartDate.getFullYear() + Math.floor(targetMonth / 12);
      const normalizedMonth = ((targetMonth % 12) + 12) % 12;
      
      const targetDay = recurrence.monthDay || baseStartDate.getDate();
      const maxDaysInTargetMonth = new Date(targetYear, normalizedMonth + 1, 0).getDate();
      const clampedDay = Math.min(targetDay, maxDaysInTargetMonth);

      nextStartDate = new Date(targetYear, normalizedMonth, clampedDay, 0, 0, 0, 0);
      break;
    }
    case 'yearly': {
      const targetYear = nextStartDate.getFullYear() + interval;
      const month = nextStartDate.getMonth();
      const day = nextStartDate.getDate();
      const maxDays = new Date(targetYear, month + 1, 0).getDate();
      const clampedDay = Math.min(day, maxDays);

      nextStartDate = new Date(targetYear, month, clampedDay, 0, 0, 0, 0);
      break;
    }
    default:
      return null;
  }

  const nextStartStr = toDateStr(nextStartDate);

  // Check on_date end condition
  if (recurrence.endType === 'on_date' && recurrence.endDate) {
    if (nextStartStr > recurrence.endDate) {
      return null;
    }
  }

  const nextEndDate = new Date(nextStartDate);
  nextEndDate.setDate(nextEndDate.getDate() + durationDays);
  const nextEndStr = toDateStr(nextEndDate);

  return {
    nextStart: nextStartStr,
    nextEnd: nextEndStr,
    nextCount: currentCount + 1
  };
}

/**
 * Generates the follow-up task instance for a recurring task.
 * Checklist items are carried over with completed: false.
 * Timers and logged hours are reset.
 */
export function generateNextRecurringTask(task: Task): Partial<Task> | null {
  if (!task.recurrence || task.recurrence.frequency === 'none') {
    return null;
  }

  const nextDates = computeNextOccurrenceDates(task);
  if (!nextDates) {
    return null;
  }

  const newChecklist = task.checklist ? task.checklist.map(item => ({
    id: 'chk_' + Math.random().toString(36).substring(2, 9),
    text: item.text,
    completed: false
  })) : undefined;

  return {
    name: task.name,
    description: task.description,
    type: task.type || 'task',
    priority: task.priority || 'none',
    assignee: task.assignee || '',
    color: task.color,
    category: task.category,
    organization: task.organization,
    contactEmail: task.contactEmail,
    contactPhone: task.contactPhone,
    role: task.role,
    start: nextDates.nextStart,
    end: nextDates.nextEnd,
    startTime: task.startTime,
    endTime: task.endTime,
    status: 'open',
    completed: false,
    checklist: newChecklist,
    estimatedHours: task.estimatedHours,
    trackedTime: 0,
    loggedHours: 0,
    timerStartedAt: null,
    recurrence: {
      ...task.recurrence,
      count: nextDates.nextCount
    }
  };
}
